<template>
    <iframe ref="viewerIframe" :src="viewerUrl"
        style="width: 100%; height: 100%; border: 0 none;"
        @load="handleIframeLoadEvent"/>
</template>

<script>
    import UriBuilder from '@services/UriBuilder';
    import Container from '@services/Container';

    const errorHandler = Container.getInstance().getErrorHandler();
    const flashMessenger = Container.getInstance().getFlashMessenger();

    // This version must match the one in webpack.config.js!
    // TODO: automate this!
    const pdfJsVersion = '2.10.378';

    export default {
        props: {
            documentUrl: {
                type: String,
                required: true,
            },
            pageMode: {
                type: String,
                default: 'thumbs',
            },
            zoom: {
                type: String,
                default: 'page-width',
            },
        },
        emits: ['pages-loaded', 'scale-changing', 'page-rendered'],
        data() {
            return {
                reloadCount: 0,
            }
        },
        computed: {
            isViewerLoadable() {
                return this.useLegacyPdfViewer !== null;
            },
            pdfJsBaseUrl() {
                return '/build/pdfjs/' + pdfJsVersion;
            },
            useLegacyPdfViewer() {
                return this.$root.useLegacyPdfViewer;
            },
            viewerUrl() {
                if (!this.isViewerLoadable) {
                    return 'about:blank';
                }

                let baseUrl = this.pdfJsBaseUrl;
                if (this.useLegacyPdfViewer) {
                    baseUrl += '/legacy';
                }
                const uriBuilder = new UriBuilder(baseUrl + '/web/viewer.html');
                uriBuilder.addParameter('file', this.documentUrl)
                uriBuilder.addParameter('reloadCount', this.reloadCount)
                uriBuilder.addAnchorParameter('pagemode', this.pageMode)
                uriBuilder.addAnchorParameter('zoom', this.zoom)
                return uriBuilder.buildUrl();
            },
        },
        created() {
            // We do not define these in the data object of Vue because
            // the object observers introduced by vue seem to break the viewer :(
            this.iframeWindow = null;
            this.viewerApp = null;
        },
        mounted() {
            this.iframeWindow = this.$refs.viewerIframe.contentWindow;
            errorHandler.attachErrorLoggerToWindow(this.iframeWindow);

            if (this.useLegacyPdfViewer !== null) {
                return;
            }

            this.$root.useLegacyPdfViewer = this.shouldUseLegacyVersion();
        },
        methods: {
            appendPdfViewerCss() {
                var cssLink = document.createElement('link');
                cssLink.href = this.pdfJsBaseUrl + '/adjustments.css';
                cssLink.rel = 'stylesheet';
                cssLink.type = 'text/css';
                this.$refs.viewerIframe.contentDocument.head.appendChild(cssLink);
            },
            enableInteractiveForms() {
                if (!this.viewerApp) {
                    flashMessenger.addError('Bei der Initialisierung der PDF-Vorschau ist ein Fehler aufgetreten.');
                    return;
                }

                this.viewerApp.preferences.get('renderInteractiveForms').then(renderInteractiveForms => {
                    if (renderInteractiveForms) {
                        // Interactive forms already active, nothing to do.
                        return;
                    }

                    this.viewerApp.preferences.set('renderInteractiveForms', true).then(
                        // Make sure PDF is reloaded after preference was changed.
                        () => this.reloadCount++
                    );
                });
            },
            getIFrameContent() {
                return this.iframeWindow;
            },
            goToPage(pageNumber) {
                if (!this.viewerApp || !this.viewerApp.eventBus) {
                    return;
                }
                // Dispatching via the internal eventBus is the correct way to trigger page navigation
                // from outside the iframe.
                this.viewerApp.eventBus.dispatch('pagenumberchanged', {
                    source: this.iframeWindow,
                    value: String(pageNumber),
                });
            },
            getPdfData() {
                if (!this.viewerApp) {
                    flashMessenger.addError('Das PDF kann nicht gespeichert werden.');
                    return new Promise(() => null);
                }
                const pdfDocument = this.viewerApp.pdfDocument;

                if (!pdfDocument.annotationStorage.size > 0) {
                    return pdfDocument.getData().then(function(data) {
                        return data;
                    });
                }

                return pdfDocument.saveDocument().then(function(data) {
                    return data;
                });
            },
            handleIframeLoadEvent() {
                if (!this.isViewerLoadable) {
                    return;
                }

                this.viewerApp = this.iframeWindow.PDFViewerApplication;
                if (!this.initializeEventBus()) {
                    this.waitForEventBus();
                }
                this.appendPdfViewerCss();
                this.enableInteractiveForms();

                // Remove the hidden password field that is used for password protected PDFs for two reasons:
                // 1. Browsers think that the last input field in a document is a username field and
                //    offers unwanted autocompletion.
                // 2. Since we do not open password protected PDFs we do not need the field.
                this.removePasswordField();
            },
            initializeEventBus() {
                if (!this.viewerApp) {
                    return false;
                }
                if (!this.viewerApp.eventBus) {
                    return false;
                }
                this.registerPagesLoadedEvent(this.viewerApp.eventBus);
                this.registerScaleChangingEvent(this.viewerApp.eventBus);
                this.registerPageRenderedEvent(this.viewerApp.eventBus);
                return true;
            },
            isOptionalChainingSupported() {
                try {
                    const test = {};
                    const isUndefined = test?.foo?.bar
                    if (isUndefined === undefined) {
                        return true
                    }
                } catch {
                    return false
                }
            },
            registerPageRenderedEvent(eventBus) {
                eventBus.on('pagerendered', () => this.$emit('page-rendered'));
            },
            registerPagesLoadedEvent(eventBus) {
                eventBus.on(
                    'pagesloaded',
                    (params) => this.$emit('pages-loaded', new URL(params.source.viewer.baseURI))
                );
            },
            registerScaleChangingEvent(eventBus) {
                eventBus.on('scalechanging', () => this.$emit('scale-changing'));
            },
            removePasswordField() {
                const passwordField = this.$refs.viewerIframe.contentDocument.getElementById('password');
                if (passwordField) {
                    passwordField.remove();
                }
            },
            shouldUseLegacyVersion() {
                return typeof this.iframeWindow.ReadableStream === 'undefined'
                    || typeof this.iframeWindow.Promise === 'undefined'
                    || typeof this.iframeWindow.Promise.allSettled === 'undefined'
                    || !this.isOptionalChainingSupported();
            },
            waitForEventBus() {
                window.setTimeout(
                    () => {
                        if (!this.initializeEventBus()) {
                            this.waitForEventBus();
                        }
                    },
                    20
                );
            },
        },
    }
</script>
